import { useMemo } from 'react';
import type { Model } from '../../types';

/**
 * The Successor picker (task 1130) on the Add Model dialog and the Model
 * page's edit state. A Model's successor is the Model the resolver runs on
 * instead once this one is withdrawn — retired, or past its shutdown date
 * (service task 1129). The picker lists the catalog Models that are not
 * retired, grouped by provider key as the Model Config form's picker is, and
 * "None" (sent as `null`) clears it. Any provider may be chosen.
 */

const SELECT_CLASSES = `block w-full px-3 py-2 border rounded-md shadow-sm sm:text-sm
  bg-white dark:bg-gray-700 text-gray-900 dark:text-white
  focus:outline-none focus:ring-1
  disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-500 dark:disabled:text-gray-400 disabled:cursor-not-allowed`;

interface SuccessorSelectProps {
  name: string;
  /** Model row id, or '' for none */
  value: string;
  onChange: (value: string) => void;
  /** The catalog; retired Models are left out unless already selected */
  models: Model[];
  /** The Model being edited, which cannot succeed itself */
  excludeId?: string;
  error?: string;
  disabled?: boolean;
}

export function SuccessorSelect({ name, value, onChange, models, excludeId, error, disabled }: SuccessorSelectProps) {
  const groups = useMemo(() => {
    const byProvider = new Map<string, Model[]>();
    for (const model of models) {
      if (model.id === excludeId) continue;
      // A stored successor retired since stays listed so the picker shows what is set.
      if (model.status === 'retired' && model.id !== value) continue;
      const key = model.provider?.key ?? 'unknown';
      byProvider.set(key, [...(byProvider.get(key) ?? []), model]);
    }
    return Array.from(byProvider.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [models, excludeId, value]);

  return (
    <div>
      <label htmlFor={name} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        Successor
      </label>
      <select
        id={name}
        name={name}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={`${SELECT_CLASSES} ${
          error
            ? 'border-red-300 dark:border-red-600 focus:border-red-500 focus:ring-red-500'
            : 'border-gray-300 dark:border-gray-600 focus:border-blue-500 focus:ring-blue-500'
        }`}
      >
        <option value="">None</option>
        {groups.map(([providerKey, providerModels]) => (
          <optgroup key={providerKey} label={providerKey}>
            {providerModels.map((model) => (
              <option key={model.id} value={model.id}>
                {model.displayName ? `${model.displayName} (${model.modelId})` : model.modelId}
                {model.status === 'deprecated' ? ' — deprecated' : model.status === 'retired' ? ' — retired' : ''}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      {error ? (
        <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>
      ) : (
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Once this Model is retired or its shutdown date passes, anything still using it runs on the successor.
        </p>
      )}
    </div>
  );
}
